import { useId } from "react";
import { Check, ChevronDown } from "lucide-react";

import partnerLogos from "@/assets/images/brand-logo/partner-logos.svg";

// MICS-163: Marketing copy (Miriam, 2026-10-13), hard-coded until it becomes admin-editable.
const HOW_IT_WORKS =
  "Installation technology is spread across PennEngineering brands, product lines and websites. The Configurator brings it into one guided tool. Describe your fastener, part and production needs, and it shows you the matching solutions, from handtools and standalone machines to robotic integration, in-die and custom systems. It then helps you compare them, configure the one you choose, and request a quote, all in one place.";

const WHY_IT_MATTERS: ReadonlyArray<{ title: string; body?: string }> = [
  {
    title: "One portfolio, one view.",
    body: "Filter every solution by fastener type, thread size, installation force and must-have capabilities such as process monitoring or robot integration.",
  },
  {
    title: "Decide on total cost, not just machine price.",
    body: "Compare solutions side by side on installation cost per part, including machine, labor and quality cost, plus operator and machine utilization. The best fit is flagged for both lowest investment and lowest installation cost.",
  },
  {
    title: "Configure with confidence.",
    body: "Select machine options that are compatible with your solution and request a quotation directly from your configuration.",
  },
  { title: "Faster to a quote." },
];

type LandingIntroProps = {
  open: boolean;
  onToggle: () => void;
};

export const LandingIntro = ({ open, onToggle }: LandingIntroProps) => {
  const panelId = useId();

  return (
    <section aria-label="What this tool does" className="shrink-0 bg-penn-900 px-3 sm:px-6 pt-4">
      <div className="rounded-2xl border border-on-navy-border bg-linear-to-r from-white/[0.08] to-white/[0.03] shadow-soft">
        <div className="flex flex-col gap-4 px-5 py-4 sm:px-6 lg:flex-row lg:items-center lg:gap-8">
          <div className="min-w-0 flex-1">
            <p className="text-label uppercase text-penn-400">What this tool does</p>
            <p className="mt-1 text-h4 text-on-navy">
              The Installation Systems Configurator matches your application to the most
              efficient installation solution across the entire PennEngineering portfolio.{" "}
              <button
                type="button"
                aria-expanded={open}
                aria-controls={panelId}
                onClick={onToggle}
                className="inline-flex items-center gap-1 rounded-sm font-medium text-penn-400 hover:text-on-navy transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-penn-400"
              >
                How it works
                <ChevronDown
                  aria-hidden
                  className={`h-4 w-4 transition-transform motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
                />
              </button>
            </p>
          </div>
          {/* Brand-logo lockup — the portfolio the promise refers to, as a single
              white-on-transparent SVG on the card. */}
          <div className="flex items-center justify-center border-t border-on-navy-border pt-4 lg:self-stretch lg:border-t-0 lg:border-l lg:pt-0 lg:pl-8">
            <img
              src={partnerLogos}
              alt="PEM, PEMSERTER, PROFIL, Haeger by PEM, MDS, Sherex"
              className="h-7 sm:h-8 lg:h-9 w-auto max-w-full object-contain"
            />
          </div>
        </div>

        {open && (
          <div
            id={panelId}
            className="grid gap-5 border-t border-on-navy-border px-5 py-5 sm:px-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-10"
          >
            <div className="grid content-start gap-3">
              <p className="text-body-lg text-on-navy">
                Compare options by total installation cost per part, configure your choice, and
                request a quote.
              </p>
              <p className="text-body text-on-navy-secondary">{HOW_IT_WORKS}</p>
            </div>
            <div>
              <h2 className="text-label uppercase text-on-navy-secondary">Why it matters</h2>
              <ul className="mt-3 grid gap-x-8 gap-y-3 sm:grid-cols-2">
                {WHY_IT_MATTERS.map(({ title, body }) => (
                  <li key={title} className="flex gap-3">
                    <Check aria-hidden className="mt-0.5 h-4 w-4 shrink-0 text-penn-400" />
                    <p className="text-body text-on-navy-secondary">
                      <span className="font-semibold text-on-navy">{title}</span>
                      {body && ` ${body}`}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
